/**
 * Tiny renderer-only signal store used to flash the Object Explorer row for a
 * just-created content-bearing workspace pin (`pinned-workspace` — F14/G17).
 *
 * The pin-creation action (Task 18) calls `set(pinId)` right after a new
 * workspace pin is written. The row for that pin (`TreeNodeContent`, Task 17)
 * reads `pinId`; when it matches its own `pinRef.id` it shows a brief ring +
 * pulse animation and scrolls itself into view, then calls `clear()` once the
 * flash has run its course (~1.5s) so a later re-render of the same row never
 * re-triggers the flash and a different pin's row is never mistakenly matched.
 *
 * Deliberately NOT folded into `pinnedObjectsStore` — this is ephemeral UI
 * signalling for one row's animation, not persisted pin state, so the pin
 * mutation actions don't need to know anything about tree-row animation.
 */
import { create } from 'zustand';

type JustPinnedState = {
  /** id of the most recently created workspace pin pending its flash, or `null` when none is pending. */
  pinId: string | null;
  /** Records a newly created pin's id so its row can flash. */
  set: (pinId: string) => void;
  /** Clears the pending flash — called by the row once it has finished animating. */
  clear: () => void;
};

export const useJustPinnedStore = create<JustPinnedState>((set) => ({
  pinId: null,
  set: (pinId) => set({ pinId }),
  clear: () => set({ pinId: null }),
}));
